import {
	Box,
	Flex,
	Input,
	InputProps,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalFooter,
	ModalHeader,
	Stack,
	Text,
} from "@caspeco/casper-ui-library";
import {
	ThemeBorderVariable,
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeRadiusVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { type JSX, ChangeEvent, useEffect, useRef, useState } from "react";
import Spinner from "./spinner";
import { useDrawerState } from "../hooks/use-drawer-state";
import DelayInput from "./delay-input";
import { useAppContext } from "../hooks/use-app-context";

interface SearchInputProps<T> extends InputProps {
	items: T[];
	renderItem: (item: T) => JSX.Element;
	searchProps: (keyof T)[];
	displayInput: keyof T;
	onSelect: (item: T) => void;
	selectedItem?: T;
	disabledItem?: (item: T) => boolean;
	isLoading?: boolean;
}

const SearchInput = <T,>({
	items,
	renderItem,
	searchProps,
	displayInput,
	onSelect,
	selectedItem,
	disabledItem,
	isLoading,
	...props
}: SearchInputProps<T>) => {
	const [searchResult, setSearchResult] = useState<T[]>([]);
	const [selectedValue, setSelectedValue] = useState<T | undefined>(selectedItem);
	const [openSearch, closeSearch, isSearchOpen] = useDrawerState();
	const { safeArea } = useAppContext();

	const search = (event: ChangeEvent<HTMLInputElement>) => {
		const input = event.target.value;

		const searchResult = items.filter((x) =>
			searchProps.some((key) => {
				return (x[key] as string).toLowerCase().includes(input.toLowerCase());
			}),
		);
		setSearchResult(searchResult);
	};

	useEffect(() => {
		setSearchResult(items);
	}, [items]);

	useEffect(() => {
		setSelectedValue(selectedItem);
	}, [selectedItem]);

	const inputRef = useRef<HTMLInputElement | null>(null);
	const delayInputRef = useRef<HTMLInputElement>(null);

	const onInputFocus = () => {
		setSearchResult(items);
		openSearch();
		inputRef.current?.blur();
		delayInputRef.current?.click();
	};

	return (
		<>
			<Input
				ref={inputRef}
				{...props}
				placeholder={props.placeholder}
				onFocus={onInputFocus}
				value={selectedValue ? (selectedValue[displayInput] as string) : ""}
				isDisabled={props.isDisabled || isLoading}
				rightElement={isLoading ? <Spinner color="currentColor" /> : null}
			/>

			<Modal isOpen={isSearchOpen} onClose={closeSearch} size="md">
				<ModalHeader
					p={ThemeSpaceVariable.Medium}
					display={"flex"}
					justifyContent={"space-between"}
					alignItems={"center"}
				>
					<Text fontSize={ThemeFontSizeVariable.XLarge} fontWeight={"bold"}>
						{props.placeholder}
					</Text>
					<ModalCloseButton />
				</ModalHeader>
				<ModalBody p={ThemeSpaceVariable.Medium}>
					<Stack
						backgroundColor={ThemeColorVariable.Surface}
						justifyContent="space-between"
						gap={ThemeSpaceVariable.Medium}
					>
						<Flex direction={"column"} overflow={"scroll"} marginTop={"auto"} gap={ThemeSpaceVariable.Small}>
							{searchResult.map((item, i) => {
								const isDisabled = disabledItem?.call(item, item) ?? false;
								return (
									<Box
										key={i}
										padding={ThemeSpaceVariable.Medium}
										border={ThemeBorderVariable.XSmall}
										borderColor={ThemeColorVariable.OnSurfaceBorder}
										borderRadius={ThemeRadiusVariable.Small}
										background={isDisabled ? ThemeColorVariable.SurfaceDisabled : "inherit"}
										onClick={() => {
											if (!isDisabled) {
												closeSearch();
												setSearchResult(items);
												setSelectedValue(item);
												onSelect(item);
											}
										}}
									>
										{renderItem(item)}
									</Box>
								);
							})}
						</Flex>
					</Stack>
				</ModalBody>
				<ModalFooter
					px={ThemeSpaceVariable.Medium}
					pt={ThemeSpaceVariable.Medium}
					pb={safeArea.bottomAsPx(ThemeSpaceVariable.Medium)}
				>
					<DelayInput
						ref={delayInputRef}
						delay={300}
						placeholder={props.placeholder}
						autoFocus
						onChange={search}
					/>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default SearchInput;
